import { recordActivity } from '../../../services/engagement';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { TbCopy, TbCheck, TbBan, TbX, TbDeviceDesktop, TbMail, TbDeviceFloppy, TbTrash } from "react-icons/tb";
import { api } from "../../lib/api";
import { useEmailTemplates } from "../../hooks/useProspecting";
import { Button, Field, Alert, Modal, Toggle, SettingRow } from "../../components";
import "./ProvisionLocalhost.css";
import { getSnapshotName } from "../../../utils/snapshotBranding";

// Provision a tenant-branded Snapshot — the localhost scan — from
// inside the organization it is for.
//
// The name is a UI label only. `scan_type: "Localhost Scan"`, the s2/api/scanner/* routes,
// the "localhost" email-template type and every stored value keep their existing names:
// renaming those would orphan every scan already on disk.
//
// This is the v1 "Generate Scanner Download Link" flow (components/LocalhostOnly.jsx)
// rebuilt on the v2 primitives. v2 had no equivalent at all: its button navigated to
// /scan-links/new, which is the EXTERNAL domain-scan link generator — a different
// product surface entirely, so the localhost flow was simply unreachable from v2.
//
// The endpoints and the payload shapes are v1's unchanged, deliberately: the backend is
// shared, and a "tidier" request body here would just be a request the server rejects.
// The organization is the context rather than a question, so v1's organization picker
// and its cross-organization token table are both gone — v1 hides them too when it is
// handed an orgid.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// This is the text clients actually receive. It is duplicated verbatim in v1's
// components/LocalhostOnly.jsx — edit both or the same feature mails two different things.
//
// Step 2 uses the same compact button label as components/DownloadScanner.jsx.
// Step 5 quotes the desktop app's own START SCANNING button.
const DEFAULT_SUBJECT = "{snapshotName} for {orgName}";
const DEFAULT_BODY =
  "The {snapshotName} your company has requested has begun.\n\n" +
  "To continue, please follow the steps below using the secure website provided. " +
  "This website was created specifically for your organization and should not be shared with anyone outside of it:\n\n" +
  "Secure Website:\n" +
  "{downloadLink}\n\n" +
  "Steps to Continue:\n" +
  "1. Open the secure website listed above in your browser.\n" +
  "2. Confirm your organization's name, then click \"Download Snapshot.\"\n" +
  "3. A file will automatically download to your Downloads folder.\n" +
  "4. Open your Downloads folder and double-click the file to start the test.\n" +
  "5. A window will pop up asking you to \"START SCANNING\", click on start scanning.\n" +
  "6. The window will then minimize, and exit when complete.\n" +
  "7. You can use your computer as normal while the scan is running.\n\n" +
  "About the {snapshotName}:\n" +
  "• It typically runs for 15 minutes to 1 hour.\n" +
  "• You can continue using your computer during this time.\n" +
  "• Once the test finishes, the window will close on its own.\n\n" +
  "Important Reminders:\n" +
  "• The analysis is completely safe. Nothing is copied, moved, or deleted from your device, network, or cloud accounts.\n" +
  "• Please have 3 to 5 other users in your organization run this test on their computers.\n" +
  "• Try to complete the analysis at least 72 hours before our next meeting.\n\n" +
  "Download Details:\n" +
  "{downloadInfo}\n\n" +
  "If you have any questions, just let us know!";

const PROVIDERS = [
  { key: "gmail",          label: "Gmail" },
  { key: "outlook",        label: "Outlook.com" },
  { key: "outlook-office", label: "Microsoft 365" },
  { key: "yahoo",          label: "Yahoo" },
  { key: "default",        label: "Default mail app" },
];

const composeUrl = (provider, to, subject, body) => {
  const s = encodeURIComponent(subject);
  const b = encodeURIComponent(body);
  switch (provider) {
    case "gmail":          return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${s}&body=${b}`;
    case "outlook":        return `https://outlook.live.com/mail/0/deeplink/compose?to=${to}&subject=${s}&body=${b}`;
    case "outlook-office": return `https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${s}&body=${b}`;
    case "yahoo":          return `https://mail.yahoo.com/mail/compose?to=${to}&subject=${s}&body=${b}`;
    default:               return `mailto:${to}?subject=${s}&body=${b}`;
  }
};

const ProvisionLocalhostModal = ({ organizationId, organizationName, onClose }) => {
  // Link options
  const [downloadLimit, setDownloadLimit] = useState(5);
  const [expiresIn, setExpiresIn] = useState(24);
  const [unlimited, setUnlimited] = useState(false);
  const [neverExpires, setNeverExpires] = useState(false);

  // Minted link
  const [link, setLink] = useState("");
  const [token, setToken] = useState("");
  const [filename, setFilename] = useState("");
  const [revoked, setRevoked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [tenant, setTenant] = useState(null);

  // Recipients and message
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [emailError, setEmailError] = useState("");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);

  const { templates, save: saveTemplate, remove: removeTemplate } = useEmailTemplates("localhost");
  // "" is the built-in default, which is not a row in the table. Everything else is a
  // template id. The select is controlled by this, so what is highlighted in the list is
  // always what is actually loaded into the fields below.
  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateMsg, setTemplateMsg] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Tenant branding and email placeholders are optional; a lookup failure
    // must not block minting a link.
    api.get("s2/api/scanner/tenant-info")
      .then((d) => { if (!cancelled) setTenant(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const snapshotName = getSnapshotName(tenant?.name || tenant?.tenant_name);
  const fullUrl = link ? `${window.location.origin}${link}` : "";

  const filled = useCallback((text) => {
    const downloadInfo =
      unlimited && neverExpires ? "• Unlimited downloads with no expiration"
      : unlimited               ? "• Unlimited downloads"
      : neverExpires            ? "• No expiration date"
      : `• ${downloadLimit} downloads • Expires in ${expiresIn} hours`;
    return (text || "")
      .replace(/{snapshotName}/g, () => snapshotName)
      .replace(/{downloadLink}/g, fullUrl)
      .replace(/{downloadLimit}/g, unlimited ? "Unlimited" : downloadLimit)
      .replace(/{expiresIn}/g, neverExpires ? "Never" : expiresIn)
      .replace(/{tenantName}/g, tenant?.name || tenant?.tenant_name || "[Tenant]")
      .replace(/{orgName}/g, organizationName || "[Organization]")
      .replace(/{downloadInfo}/g, downloadInfo);
  }, [fullUrl, unlimited, neverExpires, downloadLimit, expiresIn, tenant, organizationName, snapshotName]);

  const resolved = useMemo(
    () => ({ subject: filled(subject), body: filled(body) }),
    [filled, subject, body]
  );

  const generate = async () => {
    if (!organizationId) { setError("This organization has no id, so a link cannot be issued for it."); return; }
    setBusy(true); setError("");
    try {
      const data = await api.post("/s2/api/scanner/generate-link", {
        organization_id: organizationId,
        // v1's sentinels for "no limit" / "no expiry". The backend reads these values,
        // not a null or a flag, so they are sent exactly as v1 sends them.
        download_limit: unlimited ? 999999 : Number(downloadLimit),
        expires_in: neverExpires ? 87600 : Number(expiresIn),
        tenant_name: tenant?.name || tenant?.tenant_name,
        org_name: organizationName || "",
      });
      if (!data?.success) throw new Error(data?.error || data?.message || "Failed to generate link.");
      setLink(data.download_url);
      setToken(data.token);
      setFilename(data.filename || "");
      setRevoked(false);
    } catch (e) {
      setError(e.message || "Could not generate the download link.");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!token) return;
    setBusy(true); setError("");
    try {
      await api.del(`/s2/api/scanner/revoke/${token}`);
      setRevoked(true);
    } catch (e) {
      setError(e.message || "Could not revoke the link.");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl); recordActivity('snapshot.link_copied');
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked; the URL is on screen to copy by hand */ }
  };

  const addEmails = (text) => {
    const parts = String(text).split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!parts.length) return;
    const good = [], bad = [];
    parts.forEach((p) => {
      if (!EMAIL_RE.test(p)) bad.push(p);
      else if (!emails.includes(p) && !good.includes(p)) good.push(p);
    });
    if (good.length) setEmails((prev) => [...prev, ...good]);
    setDraft("");
    setEmailError(bad.length ? `Not a valid address: ${bad.slice(0, 3).join(", ")}${bad.length > 3 ? "…" : ""}` : "");
  };

  const send = (provider) => {
    if (!emails.length) { setEmailError("Add at least one recipient first."); return; }
    setEmailError("");
    window.open(composeUrl(provider, emails.join(","), resolved.subject, resolved.body), "_blank", "noopener");
  };

  const selected = templates.find((t) => String(t.id) === String(templateId)) || null;

  const loadTemplate = (id) => {
    setTemplateError(""); setTemplateMsg("");
    setTemplateId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    // No row means the built-in default, which is the only entry not stored server-side.
    setSubject(t ? (t.subject ?? "") : DEFAULT_SUBJECT);
    setBody(t ? (t.body ?? "") : DEFAULT_BODY);
    setTemplateName(t ? t.name : "");
  };

  // Save is an upsert against the NAME, not against whatever happens to be selected.
  //
  // The backend rejects a create whose name is already taken (409), so a plain POST
  // meant the second save of a template failed with "A template with this name already
  // exists" — which reads as saving being broken. Matching on name first turns the
  // second save into the update the user meant, and leaves creating a genuinely new
  // template to a name nobody is using.
  const storeTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    if (!body.trim()) { setTemplateError("A template needs a message body."); return; }

    setSavingTemplate(true); setTemplateError(""); setTemplateMsg("");
    try {
      const existing = templates.find((t) => t.name.trim().toLowerCase() === name.toLowerCase());
      const saved = await saveTemplate({ id: existing?.id, name, subject, body });
      // Stay on what was just written rather than snapping back to the built-in default.
      if (saved?.id) setTemplateId(String(saved.id));
      else if (existing) setTemplateId(String(existing.id));
      setTemplateMsg(existing ? `Updated "${name}".` : `Saved "${name}".`);
    } catch (e) {
      setTemplateError(e.message || "Could not save the template.");
    } finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async () => {
    if (!selected) return;
    setSavingTemplate(true); setTemplateError(""); setTemplateMsg("");
    try {
      await removeTemplate(selected.id);
      loadTemplate("");
      setTemplateMsg("Template deleted.");
    } catch (e) {
      // is_default rows are refused by the server, and that is the usual reason here.
      setTemplateError(e.message || "Could not delete the template.");
    } finally { setSavingTemplate(false); }
  };

  return (
    <Modal
      wide
      title={`Provision a ${snapshotName}${organizationName ? ` for ${organizationName}` : ""}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="v2-prov-intro">
        <span className="v2-prov-icon"><TbDeviceDesktop size={18} /></span>
        <p>
          Issue a one-time download link for the Pipsqueak scanner, then send it to
          whoever will run it on {organizationName || "this organization"}'s machines.
          Their results come back into this organization automatically.
        </p>
      </div>

      <Alert>{error}</Alert>

      {/* Step 1 — the link ------------------------------------------------- */}
      <div className="v2-prov-section">
        <div className="v2-section-title">Download link</div>

        <SettingRow name="Unlimited downloads" desc="Anyone with the link can download the scanner any number of times">
          <Toggle checked={unlimited} onChange={setUnlimited} ariaLabel="Unlimited downloads" />
        </SettingRow>
        {!unlimited && (
          <Field label="Download limit">
            <input className="v2-input" type="number" min={1} value={downloadLimit}
                   onChange={(e) => setDownloadLimit(e.target.value)} />
          </Field>
        )}

        <SettingRow name="Never expires" desc="Leave off to have the link stop working after a set time">
          <Toggle checked={neverExpires} onChange={setNeverExpires} ariaLabel="Never expires" />
        </SettingRow>
        {!neverExpires && (
          <Field label="Expires in (hours)">
            <input className="v2-input" type="number" min={1} value={expiresIn}
                   onChange={(e) => setExpiresIn(e.target.value)} />
          </Field>
        )}

        <Button variant="primary" style={{ width: "100%" }} disabled={busy} onClick={generate}>
          {busy ? "Working…" : link ? "Generate a new link" : "Generate download link"}
        </Button>
      </div>

      {link && (
        <div className="v2-prov-section">
          <div className="v2-prov-linkhead">
            <span className={`v2-status ${revoked ? "danger" : "ok"}`}>
              <span className="v2-status-dot" />{revoked ? "Revoked" : "Active"}
            </span>
            {token && <span className="v2-dim v2-mono">Token: {token.slice(0, 8)}…</span>}
          </div>

          <div className="v2-prov-linkrow">
            <input className="v2-input v2-mono" value={fullUrl} readOnly onFocus={(e) => e.target.select()} />
            <Button icon title={copied ? "Copied" : "Copy link"} onClick={copy}>
              {copied ? <TbCheck size={15} /> : <TbCopy size={15} />}
            </Button>
            <Button icon variant="danger" title="Revoke link" disabled={busy || revoked} onClick={revoke}>
              <TbBan size={15} />
            </Button>
          </div>

          <div className="v2-prov-qr">
            {/* The QR is for handing the link to someone at a desk without dictating a
                URL. It encodes the same absolute URL as the field above. */}
            <QRCodeCanvas value={fullUrl} size={120} includeMargin />
            <ul className="v2-prov-details">
              {filename && <li><span>Filename</span><b className="v2-mono">{filename}</b></li>}
              <li><span>Organization</span><b>{organizationName || "—"}</b></li>
              <li><span>Download limit</span><b>{unlimited ? "Unlimited" : `${downloadLimit} times`}</b></li>
              <li><span>Expires</span><b>{neverExpires ? "Never" : `In ${expiresIn} hours`}</b></li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 2 — sending it ------------------------------------------------ */}
      {link && !revoked && (
        <div className="v2-prov-section">
          <div className="v2-prov-sechead">
            <div className="v2-section-title">Send to</div>
            {/* Controlled, and the built-in default is a real option rather than a
                placeholder that resets itself. The old version reset the select in its
                own onChange handler, which desynced React's value tracker — so
                re-picking an entry fired no change event and silently did nothing. */}
            <select className="v2-input v2-prov-tplpick" value={templateId}
                    aria-label="Email template"
                    onChange={(e) => loadTemplate(e.target.value)}>
              <option value="">Built-in default</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div className="v2-recipients">
            {emails.length === 0 && <span className="v2-dim">No recipients yet. Type or paste addresses below.</span>}
            {emails.map((e) => (
              <span key={e} className="v2-chip">
                {e}
                <button type="button" className="v2-chip-x" aria-label={`Remove ${e}`}
                        onClick={() => setEmails((prev) => prev.filter((x) => x !== e))}>
                  <TbX size={11} />
                </button>
              </span>
            ))}
          </div>

          <div className="v2-addrow">
            <input className="v2-input" type="email" value={draft} placeholder="name@example.com"
                   onChange={(e) => setDraft(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmails(draft); } }}
                   onPaste={(e) => { e.preventDefault(); addEmails(e.clipboardData.getData("text")); }} />
            <Button onClick={() => addEmails(draft)} disabled={!draft.trim()}>Add</Button>
          </div>
          <span className="v2-hint">Paste several at once, separated by commas, spaces or new lines.</span>
          <Alert>{emailError}</Alert>

          <Field label="Subject" hint="{downloadLink}, {downloadLimit}, {expiresIn}, {tenantName}, {snapshotName}, {orgName} and {downloadInfo} are filled in when the message is composed.">
            <input className="v2-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Message">
            <textarea className="v2-input v2-longtext" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>

          <div className="v2-prov-actions">
            <Button onClick={() => setPreview(resolved)}><TbMail size={15} /> Preview</Button>
            <input className="v2-input v2-prov-tplname" placeholder="Template name" value={templateName}
                   aria-label="Template name"
                   onChange={(e) => { setTemplateName(e.target.value); setTemplateError(""); setTemplateMsg(""); }}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); storeTemplate(); } }} />
            <Button variant="primary" disabled={savingTemplate || !templateName.trim()} onClick={storeTemplate}>
              <TbDeviceFloppy size={15} />
              {savingTemplate ? "Saving…"
                : templates.some((t) => t.name.trim().toLowerCase() === templateName.trim().toLowerCase())
                  ? "Update template" : "Save template"}
            </Button>
            {selected && (
              <Button variant="danger" disabled={savingTemplate} onClick={deleteTemplate}>
                <TbTrash size={15} /> Delete
              </Button>
            )}
          </div>
          {templateMsg && <Alert tone="success">{templateMsg}</Alert>}
          <Alert>{templateError}</Alert>

          <div className="v2-prov-providers">
            {PROVIDERS.map((p) => (
              <Button key={p.key} disabled={!emails.length} onClick={() => send(p.key)}>{p.label}</Button>
            ))}
          </div>
          <span className="v2-hint">
            Opens your mail client with the message ready. Nothing is sent from Pipsqueak,
            so the email comes from your own address.
          </span>
        </div>
      )}

      {preview && (
        <Modal wide title="Email preview" onClose={() => setPreview(null)}
               footer={<Button onClick={() => setPreview(null)}>Close</Button>}>
          <Field label="Subject"><div className="v2-preview">{preview.subject}</div></Field>
          <Field label="Message"><div className="v2-preview v2-preview--body">{preview.body}</div></Field>
        </Modal>
      )}
    </Modal>
  );
};

export default ProvisionLocalhostModal;
